import { CartLinesUpdateEvent } from '@shopify/events';

const CONTAINER_SELECTOR = '[data-sy-cart-delivery-validation]';
const ROW_SELECTOR = '.cart-items__table-row';
const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"]';
const CART_ATTRIBUTE_NAME = '配送希望日';
const DEFAULT_MIN_DAYS = 2;
const DEFAULT_MAX_DAYS = 7;

if (!window.__syCartDeliveryValidationInitialized) {
  window.__syCartDeliveryValidationInitialized = true;

  let currentState = { valid: false, hasItems: false, selectedDate: '', saving: false, ready: false };
  let refreshTimer = null;
  let midnightRefreshTimer = null;
  let stateRequestId = 0;
  let pendingDateSave = false;

  function getJapanDateParts() {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date()).reduce((values, part) => {
      values[part.type] = part.value;
      return values;
    }, {});
  }

  function getJapanTodayDayNumber() {
    const parts = getJapanDateParts();
    return Math.floor(Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
    ) / 86400000);
  }

  function parseDateDayNumber(value) {
    const normalized = String(value || '').trim().replace(/\//g, '-');
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const timestamp = Date.UTC(year, month - 1, day);
    const parsed = new Date(timestamp);
    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== day
    ) return null;

    return Math.floor(timestamp / 86400000);
  }

  function formatDate(dayNumber, separator = '-') {
    return new Date(dayNumber * 86400000).toISOString().slice(0, 10).replace(/-/g, separator);
  }

  function numberProperty(value, fallback) {
    if (value === undefined || value === null || String(value).trim() === '') return fallback;
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : fallback;
  }

  function cartUrl(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path}`;
  }

  async function postCart(path, body) {
    const response = await fetch(cartUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || result.message || 'カートを更新できませんでした。');
    }
    return result;
  }

  async function fetchCart() {
    const response = await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    if (!response.ok) throw new Error('カート情報を読み込めませんでした。');
    return cart;
  }

  function getDeliveryRules() {
    const container = document.querySelector(CONTAINER_SELECTOR);
    return {
      minDays: numberProperty(container?.dataset.syDeliveryMinDays, DEFAULT_MIN_DAYS),
      maxDays: numberProperty(container?.dataset.syDeliveryMaxDays, DEFAULT_MAX_DAYS),
    };
  }

  function isSelectionInCurrentWindow(value) {
    const selectedDay = parseDateDayNumber(value);
    const rules = getDeliveryRules();
    const today = getJapanTodayDayNumber();
    return selectedDay !== null && rules.maxDays >= rules.minDays &&
      selectedDay >= today + rules.minDays && selectedDay <= today + rules.maxDays;
  }

  function getDeliveryItems(cart) {
    const rowsByKey = new Map();
    const rules = getDeliveryRules();
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => rowsByKey.set(row.dataset.key, row));

    return (cart.items || []).flatMap((line) => {
      const properties = line.properties || {};
      const propertyName = properties._sy_delivery_property_name || '配送希望日';
      const value = properties[propertyName] || '';

      const row = rowsByKey.get(line.key);
      return [{
        key: line.key,
        variantId: String(line.variant_id),
        title: row?.dataset.syDeliveryTitle || line.product_title || '商品',
        propertyName,
        properties: { ...properties },
        value,
        minDays: rules.minDays,
        maxDays: rules.maxDays,
      }];
    });
  }

  function getCommonWindow(items) {
    if (!items.length) return { minDay: 0, maxDay: -1, hasWindow: false };
    const today = getJapanTodayDayNumber();
    const minDays = Math.max(...items.map((item) => item.minDays));
    const maxDays = Math.min(...items.map((item) => item.maxDays));
    return {
      minDay: today + minDays,
      maxDay: today + maxDays,
      hasWindow: maxDays >= minDays,
    };
  }

  function isDateInWindow(value, window) {
    const selected = parseDateDayNumber(value);
    return selected !== null && window.hasWindow && selected >= window.minDay && selected <= window.maxDay;
  }

  function setStatus(message, isError = false) {
    document.querySelectorAll('[data-sy-cart-delivery-status]').forEach((status) => {
      status.textContent = message;
      status.hidden = !message;
      status.dataset.error = String(isError);
    });
  }

  function render(cart, items, window, selectedDate, message = '') {
    const hasItems = items.length > 0;
    const selectedDay = parseDateDayNumber(selectedDate);
    const inRange = selectedDay !== null && window.hasWindow && selectedDay >= window.minDay && selectedDay <= window.maxDay;
    const propertiesMatch = items.every((item) => item.value.replace(/-/g, '/') === formatDate(selectedDay ?? 0, '/'));
    const attributeValue = String(cart.attributes?.[CART_ATTRIBUTE_NAME] || '').replace(/-/g, '/');
    const attributeMatches = attributeValue === formatDate(selectedDay ?? 0, '/');
    const valid = hasItems && inRange && propertiesMatch && attributeMatches && !message;

    currentState = { valid, hasItems, selectedDate, saving: currentState.saving, ready: true };
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      container.hidden = !hasItems;
      const input = container.querySelector('[data-sy-cart-delivery-date-input]');
      const warning = container.querySelector('[data-sy-cart-delivery-warning]');
      const rangeError = container.querySelector('[data-sy-cart-delivery-range-error]');
      if (input) {
        input.min = window.hasWindow ? formatDate(window.minDay) : '';
        input.max = window.hasWindow ? formatDate(window.maxDay) : '';
        input.value = selectedDate;
        input.disabled = currentState.saving || !window.hasWindow;
      }
      if (warning) {
        warning.hidden = valid || !hasItems;
        if (!warning.hidden) {
          warning.textContent = selectedDate
            ? '選択可能期間内の日付を選択してください。'
            : '配送希望日を選択してください。';
        }
      }
      if (rangeError) {
        rangeError.hidden = window.hasWindow || !hasItems;
        if (!window.hasWindow) {
          rangeError.textContent = '配送希望日の設定で最長日が最短日より前になっています。テーマ設定の「配送希望日」を確認してください。';
        }
      }
    });

    const rangeMessage = window.hasWindow
      ? `選択可能期間：${formatDate(window.minDay, '/')}〜${formatDate(window.maxDay, '/')}`
      : 'テーマ設定で最長日を最短日以上にしてください。';
    setStatus(message || (hasItems ? rangeMessage : ''), Boolean(message) || !window.hasWindow);
    return { valid, selectedDate, hasItems };
  }

  function chooseInitialDate(cart, items, window) {
    const attributeDate = String(cart.attributes?.[CART_ATTRIBUTE_NAME] || '').replace(/-/g, '/');
    const confirmedItems = items.filter((item) => item.properties._sy_delivery_confirmed === '1');
    const confirmedDates = [...new Set(confirmedItems.map((item) => item.value.replace(/-/g, '/')).filter(Boolean))];
    const attributeDay = parseDateDayNumber(attributeDate);

    // Only carry a date forward when a previously confirmed cart line proves the
    // customer selected it. This avoids choosing a date for a brand-new cart.
    if (
      !confirmedItems.length ||
      confirmedDates.length !== 1 ||
      confirmedDates[0] !== attributeDate ||
      !isDateInWindow(attributeDate, window)
    ) return '';

    return formatDate(attributeDay);
  }

  function hasUnsyncedDate(items, selectedDate) {
    const normalizedDate = selectedDate.replace(/-/g, '/');
    return items.some((item) =>
      item.properties._sy_delivery_confirmed !== '1' ||
      item.value.replace(/-/g, '/') !== normalizedDate,
    );
  }

  async function applyExistingDateToNewLines(cart, items, window, selectedDate) {
    if (!selectedDate || !hasUnsyncedDate(items, selectedDate)) return;

    currentState.saving = true;
    document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((input) => {
      input.value = selectedDate;
      input.disabled = true;
    });
    document.querySelectorAll('[data-sy-cart-delivery-warning]').forEach((warning) => {
      warning.hidden = true;
    });
    setStatus('選択済みの配送希望日を追加商品にも保存しています…');

    try {
      cart = await synchronizeDate(items, selectedDate, cart);
      notifyThemeCartChanged(cart, items);
      render(cart, getDeliveryItems(cart), window, selectedDate);
    } catch (error) {
      render(cart, getDeliveryItems(cart), window, selectedDate,
        error.message || '選択済みの配送希望日を追加商品に保存できませんでした。');
    } finally {
      currentState.saving = false;
      document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((input) => {
        input.disabled = false;
      });
    }
  }

  async function synchronizeDate(items, selectedDate, originalCart) {
    const normalizedDate = selectedDate.replace(/-/g, '/');
    let cart = originalCart;

    cart = await postCart('cart/update.js', {
      attributes: { [CART_ATTRIBUTE_NAME]: normalizedDate },
    });

    for (const item of items) {
      const properties = {
        ...item.properties,
        _sy_delivery_min_days: String(item.minDays),
        _sy_delivery_max_days: String(item.maxDays),
        _sy_delivery_validation: '1',
        _sy_delivery_confirmed: '1',
        _sy_delivery_property_name: item.propertyName,
        [item.propertyName]: normalizedDate,
      };
      const existingTarget = (cart.items || []).find((line) =>
        String(line.variant_id) === item.variantId &&
        Object.entries(properties).every(([key, value]) => line.properties?.[key] === value),
      );
      if (existingTarget && !(cart.items || []).some((line) => line.key === item.key)) {
        item.key = existingTarget.key;
        item.properties = { ...existingTarget.properties };
        item.value = existingTarget.properties[item.propertyName] || '';
        continue;
      }
      const lineAlreadySynced = Object.entries(properties).every(([key, value]) => item.properties[key] === value);
      if (lineAlreadySynced) continue;

      cart = await postCart('cart/change.js', { id: item.key, properties });
      const updatedLine = (cart.items || []).find((line) =>
        String(line.variant_id) === item.variantId &&
        Object.entries(properties).every(([key, value]) => line.properties?.[key] === value),
      );
      if (!updatedLine) throw new Error(`${item.title}の配送希望日を保存したことを確認できませんでした。`);
      item.key = updatedLine.key;
      item.properties = { ...updatedLine.properties };
      item.value = updatedLine.properties[item.propertyName] || '';
    }

    return cart;
  }

  function notifyThemeCartChanged(cart, items) {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: items.length ? [{ id: items[0].key, quantity: 1 }] : [{ id: '', quantity: 1 }],
      promise: deferred.promise,
      detail: {
        items: cart.items,
        source: 'sy-cart-delivery-validation',
        didError: false,
      },
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'sy-cart-delivery-validation',
        didError: false,
      },
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  async function saveDate(input) {
    if (currentState.saving) return;

    // Invalidate a cart refresh that began before this selection. Otherwise a
    // stale response can rerender the empty/previous date over the new value.
    stateRequestId += 1;
    const selectedDate = input.value;
    if (!selectedDate) return;
    let cart;
    let items;
    let window;
    let refreshAfterSave = true;

    currentState.valid = false;
    currentState.selectedDate = selectedDate;
    currentState.saving = true;
    document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((field) => {
      field.value = selectedDate;
      field.disabled = true;
    });
    document.querySelectorAll('[data-sy-cart-delivery-warning]').forEach((warning) => {
      warning.hidden = true;
    });
    setStatus('配送希望日を保存中です…');

    try {
      cart = await fetchCart();
      items = getDeliveryItems(cart);
      window = getCommonWindow(items);
      if (!isDateInWindow(selectedDate, window)) {
        refreshAfterSave = false;
        const message = window.hasWindow
          ? `配送希望日は${formatDate(window.minDay, '/')}〜${formatDate(window.maxDay, '/')}から選択してください。`
          : 'テーマ設定で最長日を最短日以上にしてください。';
        render(cart, items, window, selectedDate, message);
        return;
      }

      currentState.saving = true;
      document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((field) => {
        field.value = selectedDate;
        field.disabled = true;
      });

      cart = await synchronizeDate(items, selectedDate, cart);
      notifyThemeCartChanged(cart, items);
      render(cart, getDeliveryItems(cart), window, selectedDate);
    } catch (error) {
      if (cart && items && window) {
        notifyThemeCartChanged(cart, items);
        render(cart, items, window, selectedDate, error.message || '配送希望日の保存に失敗しました。もう一度お試しください。');
      }
      else {
        setStatus(error.message || 'カートを読み込めませんでした。ページを再読み込みしてお試しください。', true);
        document.querySelectorAll('[data-sy-cart-delivery-warning]').forEach((warning) => {
          warning.hidden = false;
        });
      }
    } finally {
      currentState.saving = false;
      document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((field) => {
        field.disabled = false;
      });
      if (refreshAfterSave) scheduleRefresh();
    }
  }

  async function updateCartState() {
    // A refresh triggered as the native iOS date picker closes must not
    // overwrite the selection while its cart save is in progress.
    if (currentState.saving || pendingDateSave) return;

    const requestId = ++stateRequestId;
    try {
      const cart = await fetchCart();
      if (requestId !== stateRequestId) return;

      const items = getDeliveryItems(cart);
      const window = items.length ? getCommonWindow(items) : { minDay: 0, maxDay: -1, hasWindow: true };
      const selectedDate = chooseInitialDate(cart, items, window);
      render(cart, items, window, selectedDate);
      await applyExistingDateToNewLines(cart, items, window, selectedDate);
    } catch (error) {
      if (requestId !== stateRequestId) return;

      setStatus(error.message || '配送希望日を確認できませんでした。ページを再読み込みしてお試しください。', true);
      currentState = { valid: false, hasItems: true, saving: false, ready: true };
      document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => { container.hidden = false; });
      document.querySelectorAll('[data-sy-cart-delivery-warning]').forEach((warning) => { warning.hidden = false; });
    } finally {
      if (requestId === stateRequestId) {
        currentState.saving = false;
        document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((input) => { input.disabled = false; });
      }
    }
  }

  function scheduleRefresh() {
    currentState.ready = false;
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refreshTimer = null;
      updateCartState();
    }, 150);
  }

  function scheduleJapanMidnightCheck() {
    window.clearTimeout(midnightRefreshTimer);
    const parts = getJapanDateParts();
    const nextMidnightUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day) + 1,
    ) - 9 * 60 * 60 * 1000;
    const delay = Math.max(1000, nextMidnightUtc - Date.now() + 250);

    midnightRefreshTimer = window.setTimeout(() => {
      midnightRefreshTimer = null;
      updateCartState();
      scheduleJapanMidnightCheck();
    }, delay);
  }

  function handleCartLinesUpdate(event) {
    const refresh = () => scheduleRefresh();
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(refresh, refresh);
    } else {
      refresh();
    }
  }

  function blockInvalidCheckout(event) {
    if (currentState.ready && !currentState.hasItems) return;
    if (currentState.ready && currentState.valid && !currentState.saving) {
      if (isSelectionInCurrentWindow(currentState.selectedDate)) return;

      currentState.valid = false;
      currentState.selectedDate = '';
      document.querySelectorAll('[data-sy-cart-delivery-date-input]').forEach((input) => { input.value = ''; });
      document.querySelectorAll('[data-sy-cart-delivery-warning]').forEach((warning) => { warning.hidden = false; });
      setStatus('配送希望日が選択可能期間外になりました。新しい日付を選択してください。', true);
      scheduleRefresh();
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const warning = document.querySelector('[data-sy-cart-delivery-warning]:not([hidden]), [data-sy-cart-delivery-range-error]:not([hidden])');
    warning?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    document.querySelector('[data-sy-cart-delivery-date-input]:not([disabled])')?.focus({ preventScroll: true });
  }

  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-sy-cart-delivery-date-input]')) {
      const input = event.target;
      const today = formatDate(getJapanTodayDayNumber());
      if (input.value === today && !isSelectionInCurrentWindow(input.value)) {
        // On some iOS date pickers, focusing an empty date field can commit
        // today's date before the customer chooses an available date. Treat
        // that out-of-range default as an unselected field, not a save request.
        input.value = '';
        return;
      }

      pendingDateSave = true;
      stateRequestId += 1;
      // Let the native date control finish committing its value before the
      // save flow disables the field. This also avoids racing iOS picker UI.
      window.setTimeout(() => {
        pendingDateSave = false;
        if (input.isConnected) saveDate(input);
      }, 0);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest(CHECKOUT_SELECTOR)) blockInvalidCheckout(event);
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement && event.target.querySelector('.cart-items__table')) blockInvalidCheckout(event);
  }, true);

  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateCartState();
  });
  window.addEventListener('pageshow', () => updateCartState());

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
      node instanceof Element && (
        node.matches('.cart-items__table-row, .cart-items__wrapper, [data-sy-cart-delivery-validation]') ||
        node.querySelector('.cart-items__table-row, .cart-items__wrapper, [data-sy-cart-delivery-validation]')
      ),
    ));
    if (cartChanged) scheduleRefresh();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-sy-delivery-min-days', 'data-sy-delivery-max-days'],
  });

  updateCartState();
  scheduleJapanMidnightCheck();
}
